import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box,
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalFooter,
  Input,
  Skeleton,
  SimpleGrid,
  Container,
  Link,
  Text,
  Button,
} from '@chakra-ui/react';
import { useDebounce } from 'react-use';
import { gql, useLazyQuery } from '@apollo/client';
import Heading from '#coreComponents/Heading';
import Pagination from '#coreComponents/Pagination';
import InstitutionLogo from '#coreComponents/InstitutionLogo/InstitutionLogo';
import {
  SelectInstitutionMyInstitutionsQuery,
  // SelectInstitutionSetActiveInstitutionMutation,
} from '#types/generated/graphql';
import { useInstitution } from '..';
import { useEnvironment } from '#core/Environment';
// JME
// import { useAuthHook } from '#utils/useAuthHook';
// JME1
import { useAuth } from '#core/Auth';

const InstitutionTile: React.FC<{
  institution: SelectInstitutionMyInstitutionsQuery['myInstitutions']['institutions'][0];
  onClick: () => void;
}> = ({ institution: { name, logo }, onClick }) => {
  const imageHeight = '100px';
  return (
    <Flex
      alignItems="center"
      justifyContent="space-evenly"
      cursor="pointer"
      flexDir="column"
      border="1px solid"
      borderColor="gray.100"
      boxShadow="xs"
      onClick={onClick}
      padding={4}
      _hover={{
        borderColor: 'gray.500',
        boxShadow: 'md',
      }}
    >
      <Flex
        h={imageHeight}
        flexDir="column"
        justifyContent="center"
        alignContent="center"
      >
        <InstitutionLogo
          src={logo}
          alt={`${name} logo`}
          objectFit="contain"
          w="300px"
          h={imageHeight}
        />
      </Flex>
      <Flex flexDirection="column" alignItems="center">
        <Heading
          as="h1"
          color="gray.900"
          display="block"
          fontSize="lg"
          cursor="pointer"
          textAlign="center"
          w="100%"
          title={name}
        >
          {name}
        </Heading>
      </Flex>
    </Flex>
  );
};

const SelectInstitutionSplashScreen: React.FC<{
  validateInstitution: (id: string) => void;
}> = () => {
  const { setActiveInstitution } = useInstitution();
  const {
    actions: { logOut },
  } = useAuth(); // JME2
  const { baseUrl } = useEnvironment();

  const [searchValue, setSearchValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');

  useDebounce(
    () => {
      setDebouncedSearchValue(searchValue);
      setCurrentPage(1);
    },
    300,
    [searchValue],
  );

  const [
    findMyInstitutions,
    {
      loading: findInstitutionLoading,
      error: findInstitutionError,
      data: findInstitutionData,
    },
  ] = useLazyQuery<SelectInstitutionMyInstitutionsQuery>(gql`
    query SelectInstitutionMyInstitutions(
      $search: String!
      $page: Int!
      $perPage: Int!
    ) {
      myInstitutions(search: $search, page: $page, perPage: $perPage) {
        total
        count
        page
        perPage
        hasMore
        search
        pages
        institutions {
          id
          institutionId
          name
          logo
        }
      }
    }
  `);

  useEffect(() => {
    findMyInstitutions({
      variables: {
        search: debouncedSearchValue,
        page: currentPage,
        perPage: 12, // use 12 here as it's divisible by 1,2,3 and 4 which are our grid col values
      },
    });
  }, [debouncedSearchValue, currentPage, findMyInstitutions]);

  const selectInstitution = useCallback(
    async (institutionId) => {
      setActiveInstitution(institutionId);
    },
    [setActiveInstitution],
  );

  const pageCount = useMemo(() => {
    if (!findInstitutionData) {
      return 0;
    }
    return findInstitutionData.myInstitutions.pages;
  }, [findInstitutionData]);

  return (
    <Modal
      isOpen={true}
      closeOnOverlayClick={false}
      closeOnEsc={false}
      onClose={() => {}}
      size="full"
    >
      <ModalOverlay />
      <ModalContent m={6} px={4} py={6} minHeight="initial">
        <ModalBody>
          <Flex flexDir="column" alignItems="center" gridGap={10} pt={10}>
            <Heading as="h1">Select an institution</Heading>
            <Input
              id="institution-search"
              placeholder="Search by name"
              size="md"
              variant="outline"
              value={searchValue}
              onChange={({ target }) => setSearchValue(target.value)}
              width="100%"
              maxWidth="750px"
              px={4}
              py={6}
            />
            <Container maxW="container.xl">
              <Text
                mb={4}
                color="gray.600"
                visibility={
                  // use visibility here so hiding this doesnt message with content positioning
                  findInstitutionLoading ||
                  !findInstitutionData ||
                  findInstitutionData?.myInstitutions?.institutions?.length < 1
                    ? 'hidden'
                    : undefined
                }
              >
                Is one of your institutions missing a logo, or is the logo out
                of date?{' '}
                <Link
                  color="yellow.600"
                  href="mailto:datapoints.support@timeshighereducation.com?subject=Institution Logo Upload"
                  isExternal
                >
                  Tell us
                </Link>{' '}
                and we'll upload one for you.
              </Text>
              <SimpleGrid
                flexGrow={1}
                width="100%"
                columns={[1, 2, 3, 4]}
                spacing={6}
              >
                {findInstitutionLoading && (
                  <>
                    <Skeleton height="150px" isLoaded={false} />
                    <Skeleton height="150px" isLoaded={false} />
                    <Skeleton height="150px" isLoaded={false} />
                    <Skeleton height="150px" isLoaded={false} />
                    <Skeleton height="150px" isLoaded={false} />
                    <Skeleton height="150px" isLoaded={false} />
                    <Skeleton height="150px" isLoaded={false} />
                    <Skeleton height="150px" isLoaded={false} />
                    <Skeleton height="150px" isLoaded={false} />
                    <Skeleton height="150px" isLoaded={false} />
                    <Skeleton height="150px" isLoaded={false} />
                    <Skeleton height="150px" isLoaded={false} />
                  </>
                )}
                {!findInstitutionLoading &&
                  findInstitutionData?.myInstitutions?.institutions?.map(
                    (institution) => (
                      <InstitutionTile
                        key={institution.id}
                        institution={institution}
                        onClick={() =>
                          selectInstitution(institution.institutionId)
                        }
                      />
                    ),
                  )}
              </SimpleGrid>
              <Container
                maxW="container.md"
                flexDir="column"
                alignItems="center"
              >
                {!findInstitutionLoading &&
                  !findInstitutionError &&
                  (!findInstitutionData ||
                    findInstitutionData?.myInstitutions?.institutions?.length <
                      1) && (
                    <Box
                      textAlign="center"
                      fontWeight="bold"
                      fontSize="lg"
                      display="block"
                    >
                      No institutions found. Please change your search criteria.
                    </Box>
                  )}
                {findInstitutionError && (
                  <Text
                    color="red.500"
                    fontWeight="bold"
                    fontSize="lg"
                    display="block"
                  >
                    Sorry, there was an error querying for institutions. If this
                    problem persists please email{' '}
                    <Link
                      color="red.600"
                      href="mailto:datapoints.support@timeshighereducation.com?subject=Service%20Unavailable"
                      isExternal
                    >
                      datapoints.support@timeshighereducation.com
                    </Link>
                  </Text>
                )}
              </Container>
            </Container>
            <Box>
              {findInstitutionLoading && (
                <Text fontWeight="bold" textAlign="center">
                  Loading...
                </Text>
              )}
              {!findInstitutionLoading && (
                <Text fontWeight="bold" textAlign="center">
                  Showing {findInstitutionData?.myInstitutions.count} of{' '}
                  {findInstitutionData?.myInstitutions.total} institutions
                  results.
                </Text>
              )}
              <Pagination
                current={currentPage}
                pageCount={pageCount}
                onChange={setCurrentPage}
              />
            </Box>
          </Flex>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="link"
            colorScheme="pink"
            onClick={() => logOut({ redirectUri: baseUrl })}
          >
            Log Out
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default SelectInstitutionSplashScreen;
